import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import styles from "./sidebar.module.css";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { useUI } from "../../UIContext";
import { canList } from "./permissionUtils.js";
import {
  CUSTOM_MODULES,
  CUSTOM_MODULES_CHANGED,
  getCustomModulesApi,
  MODULE_BUILDER,
} from "../custom-module/custom-module-api.js";

//SVG Images
import Dashboard from "../../assets/svg/dashboard.svg";
import Report from "../../assets/svg/reporticon.svg";
import Leads from "../../assets/svg/leads.svg";
import Contacts from "../../assets/svg/contacts.svg";
import Accounts from "../../assets/svg/accounts.svg";
import Deals from "../../assets/svg/deals.svg";
import Inventory from "../../assets/svg/inventory.svg";
import Quote from "../../assets/svg/quotation.svg";
import Expenses from "../../assets/svg/expenses.svg";
import Calls from "../../assets/svg/calls.svg";
import Meeting from "../../assets/svg/meeting.svg";
import Sms from "../../assets/svg/sms.svg";
import Email from "../../assets/svg/email.svg";
import Announcement from "../../../src/assets/svg/announcement.svg";
import CalendarActive from "../../assets/svg/calendar-menu-active.svg";
import CalendarInActive from "../../assets/svg/calendar-menu.svg";
import Order from "../../assets/svg/order-menu-ic.svg";
import Invoice from "../../assets/svg/invoice-menu-ic.svg";
import Payment from "../../assets/svg/payment-menu-ic.svg";
import Settings from "../../assets/svg/settings.svg";
import Customize from "../../assets/svg/customize.svg";

const Sidebar = () => {
  // 🔒 Permissions & UI state
  const { rolePermissionList } = useGlobal();
  const { isSidebarCollapsed, toggleSidebar } = useUI();
  const [isSetupExpanded, setIsSetupExpanded] = useState(true);

  const getLinkClass = ({ isActive }, to) => {
    // Only highlight base path menus (like /leads, /contacts, etc.) for subroutes, but not /calendar or /report
    const collapsedExtra = isSidebarCollapsed ? ` ${styles.collapsedLink}` : "";
    if (to && to.includes("/") && to !== "/calendar" && to !== "/report") {
      // Remove only the last path segment for base
      const base = to.replace(/\/(?:[^/]+)$/, "");
      if (window.location.pathname.startsWith(base)) {
        return `${styles.active} d-flex align-items-center text-decoration-none${collapsedExtra}`;
      }
    }
    return `${isActive ? styles.active : styles.inactive} d-flex align-items-center text-decoration-none${collapsedExtra}`;
  };

  // Helper function to check if menu should be visible
  const isMenuVisible = (moduleName) => {
    return canList(rolePermissionList, moduleName);
  };

  // Modules built in the Module Builder each get a menu link; the builder
  // fires CUSTOM_MODULES_CHANGED after a save or delete so this list follows.
  const canListCustomModules = isMenuVisible(CUSTOM_MODULES);
  const [customModules, setCustomModules] = useState([]);
  useEffect(() => {
    if (!canListCustomModules) return;
    const load = async () => {
      const response = await getCustomModulesApi();
      if (response?.data?.success) setCustomModules(response.data.modules ?? []);
    };
    load();
    window.addEventListener(CUSTOM_MODULES_CHANGED, load);
    return () => window.removeEventListener(CUSTOM_MODULES_CHANGED, load);
  }, [canListCustomModules]);
  const customItems = canListCustomModules
    ? customModules.map((module) => ({ to: `/custom/${module.slug}/list`, label: module.name, icon: Customize }))
    : [];

  // Define menu items for each section
  const manageItems = [
    { to: "/contacts/list", label: "Contacts", icon: Contacts, module: "Contacts" },
    { to: "/leads/list", label: "Leads", icon: Leads, module: "Leads" },
    { to: "/opportunity/list", label: "Opportunity", icon: Deals, module: "Opportunity" },
    {
      to: "/accounts/list",
      label: "Accounts",
      icon: Accounts,
      module: "Accounts",
      // Clicking the Accounts menu starts a fresh list (clears persisted
      // search/page/filter state restored on view-page return).
      // The router state flag also resets live state when already on the page.
      onClick: () => sessionStorage.removeItem("accountListState"),
      state: { resetList: true },
    },
  ];

  const businessItems = [
    {
      to: "/quotation/list",
      label: "Quotation",
      icon: Quote,
      module: "Quotation",
      // Clicking the Quotation menu starts a fresh list (clears persisted
      // search/page/date/filter state restored on view-page return).
      // The router state flag also resets live state when already on the page.
      onClick: () => sessionStorage.removeItem("quotationListState"),
      state: { resetList: true },
    },
    {
      to: "/order/list",
      label: "Order",
      icon: Order,
      module: "Order",
      // Clicking the Order menu starts a fresh list (clears persisted
      // search/page/date/filter state restored on view-page return).
      // The router state flag also resets live state when already on the page.
      onClick: () => sessionStorage.removeItem("orderListState"),
      state: { resetList: true },
    },
    {
      to: "/invoice/list",
      label: "Invoice",
      icon: Invoice,
      module: "Invoice",
      // Clicking the Invoice menu starts a fresh list (clears persisted
      // search/page/date/filter state restored on view-page return).
      // The router state flag also resets live state when already on the page.
      onClick: () => sessionStorage.removeItem("invoiceListState"),
      state: { resetList: true },
    },
    {
      to: "/payment/list",
      label: "Payment",
      icon: Payment,
      module: "Payment",
      // Clicking the Payment menu starts a fresh list (clears persisted
      // search/tab/page/date/filter state restored on view-page return).
      // The router state flag also resets live state when already on the page.
      onClick: () => sessionStorage.removeItem("paymentListState"),
      state: { resetList: true },
    },
  ];

  const activitiesItems = [
    { to: "/calls/list", label: "Call", icon: Calls, module: "Calls" },
    { to: "/calendar", label: "Calendar", icon: CalendarInActive, module: "Calendar" },
    { to: "/report", label: "Report", icon: Report, module: "Report" },
  ];

  const setupItems = [
    {
      to: "/item/list",
      label: "Item",
      icon: Inventory,
      module: "Item",
      // Clicking the Item menu starts a fresh list (clears persisted
      // search/page state restored on view-page return).
      onClick: () => sessionStorage.removeItem("itemListState"),
      state: { resetList: true },
    },
    { to: "/module-builder/list", label: "Module Builder", icon: Settings, module: MODULE_BUILDER },
  ];

  // Calculate visible items for each section
  const visibleManageItems = manageItems.filter(({ module }) => isMenuVisible(module));
  const visibleBusinessItems = businessItems.filter(({ module }) => isMenuVisible(module));
  const visibleActivitiesItems = activitiesItems.filter(({ module }) => isMenuVisible(module));
  const visibleSetupItems = setupItems.filter(({ module }) => isMenuVisible(module));

  // Check if sections have visible items
  const hasVisibleManageItems = visibleManageItems.length > 0;
  const hasVisibleBusinessItems = visibleBusinessItems.length > 0;
  const hasVisibleActivitiesItems = visibleActivitiesItems.length > 0;
  const hasVisibleSetupItems = visibleSetupItems.length > 0;

  // Renders a single menu link. When collapsed, the label is hidden and a
  // right-side tooltip shows the label instead.
  const renderItem = ({ to, label, icon, onClick, state }) => (
    <li className="nav-item" key={to}>
      <NavLink
        to={to}
        className={(props) => getLinkClass(props, to)}
        onClick={onClick}
        state={state}
        data-tooltip-id="sidebar-tooltip"
        data-tooltip-content={isSidebarCollapsed ? label : ""}
      >
        <img src={icon} alt={label} className={styles.icon} />
        {!isSidebarCollapsed && <span>{label}</span>}
      </NavLink>
    </li>
  );

  // Section heading — hidden when collapsed (icons only).
  const renderTitle = (text, extraClass = "mb-3") =>
    !isSidebarCollapsed && (
      <small className={`d-block ps-3 ${styles.title} ${extraClass}`}>
        {text}
      </small>
    );

  return (
    <div className={`${styles.sidebarWrapper} ${isSidebarCollapsed ? styles.collapsed : ""}`}>
      {/* Collapse / expand toggle — vertically centered on the right edge.
          Lives outside the scrolling area so it is never clipped. */}
      <button
        type="button"
        onClick={toggleSidebar}
        className={styles.toggleBtn}
        title={isSidebarCollapsed ? "Expand" : "Collapse"}
        aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isSidebarCollapsed ? (
          <ChevronRight size={18} color="#3d4af5" />
        ) : (
          <ChevronLeft size={18} color="#3d4af5" />
        )}
      </button>

      <div className={`d-flex flex-column py-3 text-start bg-white shadow ${styles.sidebar}`}>
      {/* Analytics Section - Show if user has dashboard access */}
      {isMenuVisible("Dashboard") && (
        <div className="mb-4">
          {renderTitle("Analytics")}
          <ul className="nav nav-pills flex-column">
            {renderItem({ to: "/", label: "Dashboard", icon: Dashboard })}
          </ul>
        </div>
      )}

      {/* Manage Section - Show only if there are visible items */}
      {hasVisibleManageItems && (
        <div className="mb-4">
          {renderTitle("Manage")}
          <ul className="nav nav-pills flex-column">
            {visibleManageItems.map(renderItem)}
          </ul>
        </div>
      )}

      {/* Business Section - Show only if there are visible items */}
      {hasVisibleBusinessItems && (
        <div className="mb-4">
          {renderTitle("Business", "")}
          <ul className="nav nav-pills flex-column">
            {visibleBusinessItems.map(renderItem)}
          </ul>
        </div>
      )}

      {/* Activities Section - Show only if there are visible items */}
      {hasVisibleActivitiesItems && (
        <div className="mb-4">
          {renderTitle("Activities")}
          <ul className="nav nav-pills flex-column">
            {visibleActivitiesItems.map(renderItem)}
          </ul>
        </div>
      )}

      {/* Custom Section - modules built in the Module Builder */}
      {customItems.length > 0 && (
        <div className="mb-4">
          {renderTitle("Custom")}
          <ul className="nav nav-pills flex-column">
            {customItems.map(renderItem)}
          </ul>
        </div>
      )}

      {/* Setup Section - Show only if there are visible items */}
      {hasVisibleSetupItems && (
        <div className="mb-4">
          {!isSidebarCollapsed ? (
            <small
              className={`d-flex align-items-center justify-content-between ps-3 pe-2 mb-2 ${styles.title}`}
              style={{ cursor: "pointer" }}
              onClick={() => setIsSetupExpanded((prev) => !prev)}
            >
              Setup
              {isSetupExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </small>
          ) : (
            renderTitle("Setup")
          )}
          {(isSetupExpanded || isSidebarCollapsed) && (
            <ul className="nav nav-pills flex-column">
              {visibleSetupItems.map(renderItem)}
            </ul>
          )}
        </div>
      )}
      </div>

      {/* Single shared tooltip used by all links when collapsed.
          High z-index keeps it above page content (e.g. table rows). */}
      {isSidebarCollapsed && (
        <Tooltip
          id="sidebar-tooltip"
          place="right"
          positionStrategy="fixed"
          style={{ zIndex: 99999 }}
        />
      )}
    </div>
  );
};

export default Sidebar;
